<template>
  <v-navigation-drawer
    v-model="isOpen"
    temporary
    right
    :width="navigationWidth"
  >
    <div class="drawer-header" :class="getHeaderColorClass()">
      <div>
        <div v-if="appEnv === 'staging'" class="header-title-env">
          テスト環境（保守）
        </div>
        <div v-if="appEnv === 'develop'" class="header-title-env">
          テスト環境（開発）
        </div>
        <div v-if="appEnv === 'training'" class="header-title-env">
          テスト環境（習熟）
        </div>
        <div class="header-title">
          メニュー
        </div>
      </div>
      <v-btn
        variant="text"
        density="comfortable"
        class="close-icon"
        icon="mdi-close"
        aria-label="メニューを閉じる"
        @click="isOpen = false"
      />
    </div>
    <v-list v-if="isRendered">
      <!-- ユーザー情報 -->
      <v-list-item class="my-2">
        <div class="d-flex align-center">
          <div class="font-weight-bold" data-testid="user-info-label">
            ユーザ
          </div>
          <div v-if="canUseChangeAccountApplication" class="ml-auto">
            <div
              v-if="!loginUser?.isInApplication"
            >
              <CustomButton
                variant="text"
                size="sm"
                color="primary"
                @click="navigateToChangeAccountApplication()"
              >
                ユーザ情報変更
              </CustomButton>
            </div>
            <div
              v-else-if="loginUser?.isInApplication"
              class="ml-auto"
            >
              <span class="text-weak">
                変更申請中
              </span>
              <CustomButton
                variant="text"
                size="sm"
                color="primary"
                class="station-change-btn"
                @click="navigateToChangeAccountApplicationDetail()"
              >
                申請確認・取消
              </CustomButton>
            </div>
          </div>
        </div>
        <div class="ml-4" data-testid="user-name">
          {{ displayUserName }}
        </div>
        <div class="d-flex align-center mt-4">
          <div class="font-weight-bold" data-testid="working-station-label">
            作業駅
          </div>
          <div class="ml-auto">
            <CustomButton
              variant="text"
              size="sm"
              color="primary"
              class="station-change-btn"
              @click="openOperationStationModal(false)"
            >
              作業駅変更
            </CustomButton>
          </div>
        </div>
        <div class="ml-4" data-testid="working-station-name">
          {{ loginUser?.workingStation?.stationNameRyaku || '' }}
        </div>
      </v-list-item>

      <!-- ホーム -->
      <v-divider class="mt-8" />
      <v-list-item
        v-if="loginUser?.proxyLoginMode !== 'CH'"
        :class="{ 'v-list-item--active': isActive('/') }"
        @click="navigateTo('/')"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_home.png" class="icons" />
        </template>
        <v-list-item-title>ホーム</v-list-item-title>
      </v-list-item>

      <!-- 荷役作業受付 -->
      <v-divider v-if="hasPermission(PermissionsEnum.CARGO_ENTRY)" />
      <v-list-item
        v-if="hasPermission(PermissionsEnum.CARGO_ENTRY)"
        :class="{ 'v-list-item--active': isActive('/work/reception') }"
        :disabled="!canUseWorkReception"
        @click="openOperationStationModal(true)"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_forklift_primary.png" class="icons" />
        </template>
        <v-list-item-title>荷役作業受付</v-list-item-title>

        <!-- 使用できない場合に理由を表示 -->
        <template #append>
          <div v-if="!canUseWorkReception" class="warning-container">
            <span class="warning-text">{{ getWorkReceptionDisabledReason }}</span>
          </div>
        </template>
      </v-list-item>

      <!-- 荷役作業受付（代理） -->
      <v-divider v-if="hasPermission(PermissionsEnum.PROXY_LOGIN_CH)" />
      <v-list-item
        v-if="hasPermission(PermissionsEnum.PROXY_LOGIN_CH)"
        @click="openProxyLoginModal('CH')"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_forklift_primary.png" class="icons" />
        </template>
        <v-list-item-title>代理ログイン（荷役作業受付）</v-list-item-title>
      </v-list-item>

      <!-- 権限付け替え -->
      <v-divider v-if="hasPermission(PermissionsEnum.PROXY_LOGIN_MAX)" />
      <v-list-item
        v-if="hasPermission(PermissionsEnum.PROXY_LOGIN_MAX)"
        @click="openProxyLoginModal('MAX')"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_user_change.png" class="icons" />
        </template>
        <v-list-item-title>代理ログイン（管理者用）</v-list-item-title>
      </v-list-item>

      <!-- 駅マップ -->
      <v-divider />
      <v-list-item
        :class="{ 'v-list-item--active': isActive('/station/map') }"
        @click="openDisplayStationMapModal"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_map.png" class="icons" />
        </template>
        <v-list-item-title>駅エリアマップ</v-list-item-title>
      </v-list-item>

      <!-- 駅からのお知らせ -->
      <v-divider v-if="canShowStationNoticeMenu" />
      <v-list-item
        v-if="canShowStationNoticeMenu"
        :class="{ 'v-list-item--active': isActive('/station/notification') }"
        @click="navigateTo('/station/notification')"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_notifications.png" class="icons" />
        </template>
        <v-list-item-title>駅からのお知らせ</v-list-item-title>
      </v-list-item>

      <!-- 列車運行状況 -->
      <v-divider v-if="canShowTrainStatusMenu" />
      <v-list-item
        v-if="canShowTrainStatusMenu"
        :class="{ 'v-list-item--active': isActive('/station/delay') }"
        @click="navigateTo('/station/delay')"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_train.png" class="icons" />
        </template>
        <v-list-item-title>列車運行状況</v-list-item-title>
      </v-list-item>

      <!-- アカウント管理（アコーディオン） -->
      <v-list-group
        v-if="!isMobile && (
          hasPermission(PermissionsEnum.ACC_APPROVE)
          || hasPermission(PermissionsEnum.ACC_MGMT_MOD_DEL)
        )"
      >
        <template #activator="{ props }">
          <v-divider />
          <v-list-item v-bind="props">
            <template #prepend>
              <v-img src="~/assets/images/icons/ic_admin_panel.png" class="icons" />
            </template>
            <v-list-item-title>アカウント管理</v-list-item-title>
          </v-list-item>
        </template>
        <v-divider />

        <!-- サブメニュー項目 -->
        <v-list-item
          v-if="hasPermission(PermissionsEnum.ACC_APPROVE)"
          class="sub-menu-item"
          :class="{ 'v-list-item--active': isActive('/account/approval') }"
          @click="navigateTo('/account/approval')"
        >
          <v-list-item-title>アカウント管理（申請承認）</v-list-item-title>
        </v-list-item>
        <v-divider v-if="hasPermission(PermissionsEnum.ACC_MGMT_MOD_DEL)" />
        <v-list-item
          v-if="hasPermission(PermissionsEnum.ACC_MGMT_MOD_DEL)"
          class="sub-menu-item"
          :class="{ 'v-list-item--active': isActive('/account/list') }"
          @click="navigateTo('/account/list')"
        >
          <v-list-item-title>アカウント管理（変更・削除）</v-list-item-title>
        </v-list-item>
      </v-list-group>

      <!-- ログイン用QRコード出力 -->
      <v-divider v-if="!isMobile && hasPermission(PermissionsEnum.QR_OUTPUT)" />
      <v-list-item
        v-if="!isMobile && hasPermission(PermissionsEnum.QR_OUTPUT)"
        :class="{ 'v-list-item--active': isActive('/business/output-login-card') }"
        @click="navigateTo('/business/output-login-card')"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_qr_code.png" class="icons" />
        </template>
        <v-list-item-title>ログイン用QRコード出力</v-list-item-title>
      </v-list-item>

      <!-- パスワード変更（ドライバー系アカウントまたは代理ログイン（荷役作業受付）中は非表示） -->
      <v-divider v-if="isPasswordResetAvailable()" />
      <v-list-item
        v-if="isPasswordResetAvailable()"
        @click="openPasswordResetModal"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_lock.png" class="icons" />
        </template>
        <v-list-item-title>パスワード変更</v-list-item-title>
      </v-list-item>

      <!-- ユーザサポート・ご案内（アコーディオン） -->
      <v-divider />
      <v-list-group>
        <template #activator="{ props }">
          <v-list-item v-bind="props">
            <template #prepend>
              <v-img src="~/assets/images/icons/ic_help.png" class="icons" />
            </template>
            <v-list-item-title>ユーザサポート・ご案内</v-list-item-title>
          </v-list-item>
        </template>
        <v-divider />

        <!-- サブメニュー項目 -->
        <v-list-item
          class="sub-menu-item"
          @click="openFaqPdfInNewTab"
        >
          <v-list-item-title>よくある質問</v-list-item-title>
        </v-list-item>

        <v-divider />
        <v-list-item
          class="sub-menu-item"
          @click="openInquiryFormInNewTab"
        >
          <v-list-item-title>お問い合わせ</v-list-item-title>
        </v-list-item>

        <v-divider />
        <v-list-item
          class="sub-menu-item"
          @click="openSiteDescriptionPdfInNewTab"
        >
          <v-list-item-title>操作マニュアル</v-list-item-title>
        </v-list-item>

        <v-divider />
        <v-list-item
          class="sub-menu-item"
          @click="openTermsOfServicePdfInNewTab(true)"
        >
          <v-list-item-title>利用規約</v-list-item-title>
        </v-list-item>
      </v-list-group>

      <!-- ログアウト -->
      <v-divider />
      <v-list-item
        v-if="loginUser?.proxyLoginMode"
        @click="handleProxyLogout"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_logout.png" class="icons" />
        </template>
        <v-list-item-title>代理ログイン終了</v-list-item-title>
      </v-list-item>
      <v-list-item
        v-else
        @click="handleLogout"
      >
        <template #prepend>
          <v-img src="~/assets/images/icons/ic_logout.png" class="icons" />
        </template>
        <v-list-item-title>ログアウト</v-list-item-title>
      </v-list-item>
      <v-divider />
    </v-list>
    <!-- 作業駅変更モーダル -->
    <OperationStationModal
      ref="operationStationModalRef"
      @completed="onOperationStationChangeCompleted"
      @canceled="onOperationStationChangeCanceled"
    />
    <!-- 駅マップ表示駅選択モーダル -->
    <DisplayStationMapModal
      ref="displayStationMapModalRef"
      @canceled="closeDisplayStationMapModal"
    />
    <!-- 代理ログインモーダル -->
    <ProxyLoginModal
      ref="proxyLoginModalRef"
      @completed="onProxyLoginCompleted"
    />
    <!-- パスワード再設定モーダル -->
    <PasswordResetModal
      ref="passwordResetModalRef"
      @canceled="closePasswordResetModal"
      @confirmed="closePasswordResetModal"
    />
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import DisplayStationMapModal from '../modal/DisplayStationMapModal.vue';
import OperationStationModal from '~/components/modal/OperationStationModal.vue';
import ProxyLoginModal from '../modal/proxy/ProxyLoginModal.vue';
import { useFile } from '~/composables/common/useFile';
import { MessageCodeEnum } from '~/constants/MessageCode';
import type { ProxyLoginMode } from '~/types/common/proxyLoginMode';
import { AccountTypeEnum } from '~/constants/AccountType';
import CustomButton from '../common/parts/CustomButton.vue';
import { PermissionsEnum } from '~/constants/Permission';
import PasswordResetModal from '../modal/auth/PasswordResetModal.vue';

const DISPLAY_ID = 'B-02';

const router = useRouter();

const route = useRoute();
const isRendered = ref(false);
/** 作業駅選択モーダルの参照 */
const operationStationModalRef = ref<InstanceType<typeof OperationStationModal>>();
/** 駅マップ表示駅選択モーダルの参照 */
const displayStationMapModalRef = ref<InstanceType<typeof DisplayStationMapModal>>();

// v-modelとの双方向バインディング
const isOpen = defineModel<boolean>();

// デバイス情報の取得
const { isMobile } = useDevice();

// 駅エリア判定に関する業務条件
const { canUseWorkReceptionByStationArea } = useStationAreaConditions();

// ファイルダウンロード系
const {
  openTermsOfServicePdfInNewTab,
  openFaqPdfInNewTab,
  openSiteDescriptionPdfInNewTab,
} = useFile(DISPLAY_ID);
// お問い合わせ
const {
  openInquiryFormInNewTab,
} = useInquiry();

const { appEnv } = useRuntimeConfig().public;

/** ナビゲーションバーの幅 */
const navigationWidth = computed(() => {
  // モバイル端末の場合は端末幅
  if (isMobile.value) {
    return window.innerWidth;
  }
  // モバイル端末以外の場合は固定幅
  else {
    return 400;
  }
});

// 荷役作業受付が利用可能かどうか
const canUseWorkReception = computed(() => {
  // 作業駅が選択されていなければ利用不可
  if (!loginUser.value?.workingStation?.stationCd) {
    return false;
  }

  return canUseWorkReceptionByStationArea.value;
});

/** 代理ログイン（CH）中かどうか */
const isProxyLoginCh = computed(() => loginUser.value?.proxyLoginMode === 'CH');

/** 駅からのお知らせメニューを表示できるかどうか */
const canShowStationNoticeMenu = computed(() => {
  return hasPermission(PermissionsEnum.STATION_NOTICE) && !isProxyLoginCh.value;
});

/** 列車運行状況メニューを表示できるかどうか */
const canShowTrainStatusMenu = computed(() => {
  return hasPermission(PermissionsEnum.TRAIN_STATUS) && !isProxyLoginCh.value;
});

// ナビゲーション関数を拡張して条件チェックを追加
const navigateTo = (path: string) => {
  // 荷役作業受付の場合の特別処理
  if (path === '/work/reception') {
    // 利用可能かどうかをチェック
    if (!canUseWorkReception.value) {
      return;
    }

    // トラック情報が設定されているかチェック
    const hasTruckInfo = !!loginUser.value?.truck?.truckNo;

    // ドロワーを閉じる
    isOpen.value = false;

    setTimeout(() => {
      // トラック情報がない場合は車番入力画面へ、ある場合は荷役作業受付へ
      if (!hasTruckInfo) {
        router.push('/work/updateTruckNumber');
      }
      else {
        router.push('/work/reception');
      }
    }, 300);

    return;
  }

  // 通常のナビゲーション処理
  isOpen.value = false;
  setTimeout(() => {
    router.push(path);
  }, 300);
};

// 荷役作業受付が使用できない理由を返す
const getWorkReceptionDisabledReason = computed(() => {
  return canUseWorkReception.value ? '' : 'は駅構内でのみ利用可能です';
});

// クライアントサイドでマウント完了後にレンダリングフラグをオン
onMounted(() => {
  isRendered.value = true;
});

// ユーザーストアのインスタンスを取得
const loginUserStore = useLoginUserStore();
const { hasPermission } = loginUserStore;
const { loginUser, displayUserName } = storeToRefs(loginUserStore);

const { showClientMessage } = useMessage();
// ログアウト処理を実行する関数
const handleLogout = () => {
  showClientMessage(
    DISPLAY_ID,
    MessageCodeEnum.CONFIRM_LOGOUT,
    true,
    async () => await useAuth(DISPLAY_ID).logout(),
  );
};

// 現在のルートがアクティブかどうかを確認
const isActive = (path: string) => {
  return route.path === path;
};

/** 荷役作業受付画面遷移の直前かどうか */
let isBeforeNavigateToWorkReceptionFlg = false;
/** 代理ログイン（CH）中かどうか */
let isInChProxyLoginProcess = false;

/**
 * ユーザ変更申請機能が利用可能かどうか
 */
const canUseChangeAccountApplication = computed(() => {
  // 代理ログイン中は利用不可
  if (loginUser.value?.proxyLoginMode) {
    return false;
  }

  // Webアカウント申請（変更）の権限がない場合は利用不可
  if (!hasPermission(PermissionsEnum.WEB_ACC_APPLY_MOD)) {
    return false;
  }

  // 指定されたアカウント区分以外の場合は利用不可
  return Object.values(AccountTypeEnum).some((accountType) => {
    if (loginUser.value?.accountType === accountType) {
      return true;
    }
  });
});

/**
 * 作業駅選択モーダルを開く
 *
 * @param isBeforeNavigateToWorkReception 荷役作業受付画面遷移の直前かどうか
 */
const openOperationStationModal = async (isBeforeNavigateToWorkReception?: boolean) => {
  isBeforeNavigateToWorkReceptionFlg = isBeforeNavigateToWorkReception ?? false;
  if (operationStationModalRef.value) {
    await operationStationModalRef.value.open();
  }
};

/**
   * 作業駅選択モーダルで処理が完了したときの処理
   *
   * 荷役作業受付画面遷移前の場合、車番変更画面に遷移する
   */
const onOperationStationChangeCompleted = () => {
  operationStationModalRef.value?.close();
  if (isBeforeNavigateToWorkReceptionFlg) {
    router.push({
      path: '/work/updateTruckNumber',
      query: { navigateTo: '/work/reception' },
    });
  }
};

/**
   * 作業駅選択モーダルで処理がキャンセルされたときの処理
   *
   * 代理ログイン中の場合、代理ログインを終了する。
   * 解除に失敗した場合も再操作できるよう、解除に成功してからダイアログを閉じる。
   */
const onOperationStationChangeCanceled = async () => {
  if (isInChProxyLoginProcess) {
    await useAuth(DISPLAY_ID).endProxyLogin();
    isInChProxyLoginProcess = false;
  }
  operationStationModalRef.value?.close();
};
/**
 * 駅マップ表示駅選択モーダルを開く
 */
const openDisplayStationMapModal = async () => {
  if (displayStationMapModalRef.value) {
    await displayStationMapModalRef.value.open();
  }
};

/**
 * 駅マップ表示駅選択モーダルを閉じる
 */
const closeDisplayStationMapModal = () => {
  if (displayStationMapModalRef.value) {
    displayStationMapModalRef.value.close();
  }
};

const getHeaderColorClass = () => {
  switch (appEnv) {
    case 'staging':
      return 'bg-color-stg';
    case 'develop':
      return 'bg-color-dev';
    case 'training':
      return 'bg-color-training';
    default:
      return 'bg-white';
  }
};

/** 代理ログインモーダルの参照 */
const proxyLoginModalRef = ref<InstanceType<typeof ProxyLoginModal>>();

/**
 * 代理ログインモーダルを開く
 */
const openProxyLoginModal = async (mode: ProxyLoginMode) => {
  if (proxyLoginModalRef.value) {
    isInChProxyLoginProcess = mode === 'CH';
    // マスタ取得エラーをグローバルエラーハンドラへ伝播させるためawaitする
    await proxyLoginModalRef.value.open(mode);
  }
};

/**
 * 代理ログイン完了時の処理
 */
const onProxyLoginCompleted = () => {
  // 代理ログイン（CH）の場合は作業駅変更モーダルを開く
  if (isInChProxyLoginProcess) {
    openOperationStationModal(true);
  }
  // それ以外の場合（代理ログイン（MAX））はホームに遷移する
  else {
    isOpen.value = false;
    router.push('/');
  }
};

const { startLoad, endLoad } = useLoadingStore();
/**
 * 代理ログインを解除し、ホームに戻る
 */
const handleProxyLogout = () => {
  showClientMessage(
    DISPLAY_ID,
    MessageCodeEnum.CONFIRM_PROXY_LOGOUT,
    true,
    async () => {
      startLoad();
      try {
        await useAuth(DISPLAY_ID).endProxyLogin();
        isOpen.value = false;
        router.push('/');
      }
      finally {
        endLoad();
      }
    },
  );
};

/** パスワード再設定モーダルの参照 */
const passwordResetModalRef = ref<InstanceType<typeof PasswordResetModal>>();

/**
 * パスワード再設定モーダルを開く
 */
const openPasswordResetModal = () => {
  if (passwordResetModalRef.value) {
    passwordResetModalRef.value.open();
  }
};

/**
 * パスワード再設定モーダルを閉じる
 */
const closePasswordResetModal = () => {
  if (passwordResetModalRef.value) {
    passwordResetModalRef.value.close();
  }
};

/**
 * パスワード再設定が可能かどうかを返す
 *
 * 代理ログイン中は不可
 *
 * @return パスワード再設定が可能な場合はtrue、そうでない場合はfalse
 */
const isPasswordResetAvailable = () => {
  return !loginUser.value?.proxyLoginMode;
};

/**
 * アカウント変更画面に遷移する
 */
const navigateToChangeAccountApplication = () => {
  let path = '';
  switch (loginUser.value?.accountType) {
    case AccountTypeEnum.DRIVER:
      path = '/account/application/change/truck-driver';
      break;
    case AccountTypeEnum.DRIVER_GUEST:
      path = '/account/application/change/guest-truck-driver';
      break;
    case AccountTypeEnum.OFFICE:
      path = '/account/application/change/office';
      break;
    case AccountTypeEnum.JRF:
      path = '/account/application/change/jrf';
      break;
    case AccountTypeEnum.PARTNER:
      path = '/account/application/change/partner';
      break;
    case AccountTypeEnum.CORPORATION:
      path = '/account/application/change/corporation';
      break;
    default:
      showClientMessage(
        DISPLAY_ID,
        MessageCodeEnum.INVALID_ACCOUNT_TYPE_SELECTED,
        false,
      );
      return;
  }

  router.push(path);
};

/**
 * アカウント変更申請詳細画面に遷移する
 */
const navigateToChangeAccountApplicationDetail = () => {
  let path = '';
  switch (loginUser.value?.accountType) {
    case AccountTypeEnum.DRIVER:
      path = '/account/application/detail/truck-driver/me';
      break;
    case AccountTypeEnum.DRIVER_GUEST:
      path = '/account/application/detail/guest-truck-driver/me';
      break;
    case AccountTypeEnum.OFFICE:
      path = '/account/application/detail/office/me';
      break;
    case AccountTypeEnum.JRF:
      path = '/account/application/detail/jrf/me';
      break;
    case AccountTypeEnum.PARTNER:
      path = '/account/application/detail/partner/me';
      break;
    case AccountTypeEnum.CORPORATION:
      path = '/account/application/detail/corporation/me';
      break;
    default:
      showClientMessage(
        DISPLAY_ID,
        MessageCodeEnum.INVALID_ACCOUNT_TYPE_SELECTED,
        false,
      );
      return;
  }

  router.push(path);
};
</script>

<style scoped lang="scss">
@use '~/assets/scss/_base.scss' as vars;

// 色の定義
$color-primary: vars.$color-primary;
$color-text-secondary: rgb(0 0 0 / 60%);
$color-text-primary: rgb(0 0 0 / 87%);

// ドロワーヘッダー
.drawer-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: vars.$header-height;
  padding: 0 16px;
  border-bottom: 1px solid rgba(vars.$color-border, 0.7);

  .header-title {
    font-size: 20px;
    font-weight: bold;
    text-align: center;
  }

  .header-title-env {
    font-size: 1rem;
    font-weight: 300;
    text-align: center;
  }

  .close-icon {
    position: absolute;
    right: 16px;
    font-size: 20px !important;
    color: $color-primary !important;
    cursor: pointer;

    &:hover {
      color: $color-primary !important;
    }
  }
}

.text-weak {
  color: vars.$color-border;
}

// 作業駅変更ボタン
.station-change-btn {
  height: 36px !important;
}

// メニュー項目のスタイリング
.v-list-item {
  min-height: 56px;
  padding: 0 16px;
  font-size: 16px;

  /* アイコンとテキストの間隔を狭める */
  :deep(.v-list-item__prepend) {
    margin-right: -16px;
  }
}

// v-list-item-titleを外に出す（特異性の問題を解消）
.v-list-item .v-list-item-title {
  padding-left: 4px;
  font-size: 16px;
}

// アクティブな状態
.v-list-item.v-list-item--active {
  background-color: rgba($color-primary, 0.1);
}

.v-list-item.v-list-item--active .v-list-item-title {
  font-weight: bold;
  color: $color-primary;
}

// ドロワーセパレーター
.v-divider {
  margin-top: 0;
  margin-bottom: 0;
  border-color: rgba(vars.$color-border, 0.7);
  opacity: 0.4;
}

// ローディング表示用のスタイル
.loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.menu-icon {
  font-size: 24px;
  color: $color-primary;
  opacity: 1 !important; // 不透明度を100%に固定
}

// アコーディオン展開時のスタイル
:deep(.v-list-group__header.v-list-item--active) {
  background-color: rgba($color-primary, 0.05);
}

// 展開アイコンの色を設定
:deep(.v-list-group__header__append-icon) {
  color: $color-primary;
}

// サブメニューのアイコンスタイル(アコーディオンの矢印)
:deep(.v-list-group .v-list-item__append .v-icon) {
  font-size: 32px !important;
  color: $color-primary !important;
  opacity: 1.0 !important;
}

// サブメニューのスタイル
.sub-menu-item {
  min-height: 48px;
  padding-left: 56px !important;
}

.sub-menu-icon {
  margin-right: -8px;
  font-size: 18px;
  color: $color-primary;
  opacity: 0.9;
}

.icons {
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 1rem;
}
</style>
